import IconCopy from "@shared/components/icons/Copy"
import IconGlobeShare from "@shared/components/icons/GlobeShare"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { createQrCode } from "cc-frontend/lib/share-link"
import { useRef, useState } from "react"
// The same globe the card menu's "Show Card on Class Website" row and the public-card notice draw.
import IconDesignFileTextImage from "@shared/components/icons/DesignFileTextImage"
import IconQrCode from "@shared/components/icons/QrCode"
import { ChoiceCards } from "@shared/components/menus/views/ChoiceCards"
import { ViewShell } from "@shared/components/menus/views/ViewShell"
import { Globe02 } from "@untitledui/icons"
import { Link } from "react-aria-components"

type LinkVersion = "full" | "classWebsite"

/**
 * "Here's the link" — share a link to the lesson, as either the full lesson or the class
 * website version (only the cards marked "Show on Class Website"). Each version's link and
 * its Copy / QR actions live inside that version's card, revealed when it is picked.
 */
export function ShareLinkView({
  links,
  qrFileName,
}: {
  links: { full: string; classWebsite: string }
  /** Filename (sans extension) for the downloaded QR code image. */
  qrFileName: string
}) {
  const [version, setVersion] = useState<LinkVersion>("full")
  const [copied, setCopied] = useState(false)

  // qr-code-styling's own `download()` appends an <a> to <body> and clicks it,
  // which has caused problems with focus-trap in the past.
  // Append our own <a> inside this component instead.
  const downloadAnchorContainer = useRef<HTMLDivElement>(null)

  const copyLink = (link: string) => {
    void navigator.clipboard.writeText(link).then(() => setCopied(true))
  }

  const downloadQrCode = async (link: string) => {
    const qr = createQrCode(link)
    const element = await qr._getElement("png")
    if (!element) return
    const dataUri = (element as HTMLCanvasElement).toDataURL("image/png")

    const anchor = document.createElement("a")
    anchor.download = qrFileName
    anchor.href = dataUri
    downloadAnchorContainer.current?.append(anchor)
    anchor.click()
    anchor.remove()
  }

  // Each card carries its own version's link: during the pick's crossfade both details are
  // painted at once, and the card letting go must keep showing its URL, not the incoming one.
  const detailFor = (detailVersion: LinkVersion) => (
    <LinkAndActions
      link={links[detailVersion]}
      copied={copied && detailVersion === version}
      onCopy={() => copyLink(links[detailVersion])}
      onDownloadQrCode={() => void downloadQrCode(links[detailVersion])}
    />
  )

  return (
    <ViewShell>
      <div className="flex flex-col px-5 pt-4 pb-5">
        <ChoiceCards<LinkVersion>
          ariaLabel="Which version of the lesson to share"
          value={version}
          onChange={(newVersion) => {
            setVersion(newVersion)
            setCopied(false)
          }}
          options={[
            {
              value: "full",
              label: "Full Lesson",
              description: "All the parts will show up",
              icon: IconDesignFileTextImage,
              detail: detailFor("full"),
            },
            {
              value: "classWebsite",
              label: "Class Website Version",
              description: "Only the parts marked “Show on Class Website” show up",
              icon: IconGlobeShare,
              detail: detailFor("classWebsite"),
            },
          ]}
        />

        {/* Off-screen host for the QR download <a> (see downloadQrCode). */}
        <div ref={downloadAnchorContainer} className="hidden" />
      </div>
    </ViewShell>
  )
}

/**
 * The picked version's link, then its two actions. ChoiceCards reveals this inside the picked
 * card but beside the radio's <label>, so a link and buttons are valid here and pressing them
 * never reaches the radio.
 */
function LinkAndActions({
  link,
  copied,
  onCopy,
  onDownloadQrCode,
}: {
  link: string
  copied: boolean
  onCopy: () => void
  onDownloadQrCode: () => void
}) {
  return (
    <span className="flex flex-col gap-2.5">
      {/* The Link. */}
      <span
        data-test="link field"
        // The tooltip lives here rather than on the link: react-aria's Link takes no title.
        title={link}
        className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-base leading-6 font-normal text-gray-900 shadow-xs"
      >
        <Globe02 className="size-4 shrink-0 text-gray-500" />

        <Link
          href={link}
          target="_blank"
          rel="noopener"
          aria-label="Link to this lesson"
          className="min-w-0 truncate text-sky-700 no-underline outline-hidden hover:underline data-focus-visible:outline-2 data-focus-visible:outline-offset-2 data-focus-visible:outline-focus-ring"
        >
          {link}
        </Link>
      </span>

      {/* Actions. They wrap rather than overflow: "Download QR Code" can't shrink below its
          own label, and two of these only fit side by side in a menu wider than this one.
          Not `view actions`: that marks a ViewShell's footer row, which menu-queries scopes
          `popoverButton` to. These live inside a card, so they carry their own name. */}
      <span data-test="share link actions" className="flex flex-wrap gap-3">
        <Button
          color="secondary"
          size="md"
          className="flex-1"
          iconLeading={IconCopy}
          onPress={onCopy}
        >
          {copied ? "Copied!" : "Copy Link"}
        </Button>
        <Button
          color="secondary"
          size="md"
          className="flex-1"
          iconLeading={IconQrCode}
          onPress={onDownloadQrCode}
        >
          Download QR Code
        </Button>
      </span>
    </span>
  )
}
